import { useState } from 'react';

import lsKey, { type LocalStorageKeyInput } from './base/localStorage.key';
import ls from './base/localStorage';

import useEvent from '../useEvent';
import useLocalStorageUtils from './useLocalStorageUtils';
import useSubscribeToStorageEvent from './notify/useSubscribeToStorageEvent';

type LocalStorageAction<T> = (prevValue: T) => T | null;

type LocalStorageOptions = {
  subscribe?: boolean;
};

const isEqualToStorageString = <T>(value: T, compareTo: string | null, fallbackCompareTo?: T) => {
  const toCompare = compareTo ?? ls.parser.in(fallbackCompareTo);

  return ls.parser.in(value) === toCompare;
};

const isEqual = <T>(value: T, compareTo: T | null, fallbackCompareTo?: T) => {
  const compareToString = compareTo == null ? null : ls.parser.in(compareTo);

  return isEqualToStorageString(value, compareToString, fallbackCompareTo);
};

const useLocalStorage = <T>(
  keyInput: LocalStorageKeyInput,
  initialValue: T,
  options?: LocalStorageOptions,
) => {
  const key = lsKey.create(keyInput);

  const getInitialState = () => ({
    key,
    value: key ? ls.get<T>(key, initialValue) : initialValue,
  });

  const [state, setState] = useState(getInitialState);

  if (key !== state.key) setState(getInitialState);

  const lsUtils = useLocalStorageUtils<T>(key, {
    onRemove: () =>
      setState((prevState) =>
        isEqual(prevState.value, null, initialValue) ? prevState : { key, value: initialValue },
      ),
    onSet: (nextValue) =>
      setState((prevState) =>
        isEqual(prevState.value, nextValue, initialValue) ? prevState : { key, value: nextValue },
      ),
  });

  const { get, remove, set } = lsUtils;

  useSubscribeToStorageEvent({
    enabled: !!key && !!options?.subscribe,
    localStorageKey: key,
    onEvent: (newValue) =>
      setState((prevState) =>
        isEqualToStorageString(prevState.value, newValue, initialValue)
          ? prevState
          : { key, value: ls.parser.out(newValue, initialValue) },
      ),
  });

  const getNextValueByAction = (action: LocalStorageAction<T>) => {
    const prevValue = key ? get() : state.value;

    return action(prevValue ?? initialValue);
  };

  // should not be used during render (nor any base method from utils), use the utils.useSync instead if no other option
  const setValueAndStorage = useEvent((nextValue: LocalStorageAction<T> | T | null) => {
    const toStore = nextValue instanceof Function ? getNextValueByAction(nextValue) : nextValue;

    if (toStore == null) remove();
    else set(toStore);
  });

  return [state.value, setValueAndStorage, lsUtils] as const;
};

export type { LocalStorageOptions };

export default useLocalStorage;
